import { ElementState } from '@/elements/base/ElementState';
import { ICompositeProductApi } from '@canvas-logic/clp-sdk';

import {
  AttributeKey,
  ComponentKey,
  createAttributeValue,
  hasAttributeValue,
  IAttributeTextData,
  IAttributeValueTextData,
} from '@canvas-logic/clp-domain';
import { IUICfgAttribute } from '@canvas-logic/clp-api-types';
import { attributeLookupBehaviour } from '@/ui-kit/domain/lookup/behaviours';
import { AttributeLookup } from '@/ui-kit/domain/lookup/AttributeLookup';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';

let id = 0;

export abstract class AttributeStateBase<T extends IUICfgAttribute, TValue = unknown> extends ElementState {
  public id = 'attribute-view-state-' + ++id;
  public readonly lookup: AttributeLookup;
  public visible = true;
  protected dependentAttributes: any[] = [];
  public viewElement: T;
  public textData: IAttributeTextData;
  readonly componentKey: ComponentKey;
  readonly attributeKey: AttributeKey;

  value: TValue | null = null;
  availableValues: TValue[] = [];
  loading = false;

  get selectedTextData(): IAttributeValueTextData | undefined {
    const data = this.textData.values.find(
      (value) =>
        this.lookup.current &&
        hasAttributeValue(this.lookup.current?.value) &&
        this.lookup.current?.value?.value == value.value,
    );
    return data;
  }

  constructor(public readonly api: ICompositeProductApi, public readonly element: T) {
    super();

    this.viewElement = element;

    const source = this.viewElement.source;
    this.componentKey = source.componentKey;
    this.attributeKey = source.attributeKey;
    this.visible = this.api.isAvailable(this.componentKey);

    const lookup = new AttributeLookup(this.api, this.componentKey, this.attributeKey);
    this.lookup = lookup;

    // TODO:
    // this.dependentAttributes = this.api.getDependentAttributesForAttribute(this.componentKey, source.attributeKey);
    attributeLookupBehaviour(lookup, this);
    this.textData = this.readTextData();

    makeObservable(this, {
      visible: observable,
      selectedTextData: computed,
      setVisibility: action.bound,
      textData: observable,
      lookup: observable,
    });
  }

  /**
   * Initializes standard value state (value, availableValues, loading) with MobX annotations.
   * Call this in the constructor of standard attribute state classes (Text, Boolean, Integer, Decimal).
   * Do NOT call this in classes with custom state management (e.g., RalAttributeState).
   */
  protected initValueState() {
    this.updateCurrent();
    if (this.value) {
      this.availableValues = [this.value];
    }

    makeObservable(this, {
      loading: observable,
      availableValues: observable,
      value: observable,
      update: action.bound,
      loadAvailableValues: action.bound,
      setValue: action.bound,
    });

    this.update().then(() => this.loadAvailableValues());
  }

  setVisibility(visibility: boolean) {
    this.visible = visibility;
  }

  /**
   * Attribute labels and swatch images are per-product (ADR-0051), so the text
   * data is read for the product currently filling this component. Falls back
   * to the component-wide union when no product is selected yet.
   */
  protected readTextData(): IAttributeTextData {
    const productKey = this.api.getComponentProductData(this.componentKey)?.productKey;
    return this.api.getComponentAttributeData(this.componentKey, this.attributeKey, productKey);
  }

  async update() {
    this.updateVisibility();
    if (this.visible) {
      this.loading = true;
      this.loadAvailableValues();
      runInAction(() => {
        this.loading = false;
        // The component's product may have just changed, which changes which
        // labels and images apply.
        this.textData = this.readTextData();
        this.updateCurrent();
      });
    }
  }

  async setValue(newValue: string | number | boolean) {
    const attributeValue = createAttributeValue(newValue);
    await this.api.setAttributeValue(this.componentKey, this.attributeKey, attributeValue);
    await this.update();
    this.handleUpdate();
  }

  async loadAvailableValues() {
    const values = await this.api.loadCompatibleAttributeValues(this.componentKey, this.attributeKey);

    runInAction(() => {
      this.availableValues = [];
      for (const attributeValue of values) {
        if (hasAttributeValue(attributeValue)) {
          if (typeof attributeValue.value !== 'undefined') {
            this.availableValues.push(attributeValue.value as TValue);
          }
        }
      }
    });
  }

  updateVisibility() {
    this.visible = this.api.isAvailable(this.componentKey);
  }

  protected handleUpdate() {
    this.notify({
      event: 'updateDependentAttributes',
      attributes: [],
      target: this,
    });
  }

  protected updateCurrent() {
    const current = this.api.getAttributeValue(this.componentKey, this.attributeKey);
    // `current` may be the valueless `{ custom: true }` member of ICfgAttributeValue,
    // which used to read as `undefined` via optional chaining — keep that exact result.
    this.value = (current && hasAttributeValue(current) ? current.value : undefined) as TValue | null;
  }
}
